import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Refresh as RefreshIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import productLinksService from '../services/ProductLinksService'; // src/services/productLinksService.ts
import * as XLSX from 'xlsx';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import { AccountCircle as AccountIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ShopifyMultiStoreSection from '../components/Shopifymultistoresection';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const { 
    shopeeAuthenticated, 
    shopifyAuthenticated, 
    getShopeeAuthUrl, 
    disconnectShopee,
    saveShopifyCredentials,
    disconnectShopify,
    logout,             // Add this
    currentUser,        // Add this
    isAuthenticated     // Add this
  } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate(); // Add this

  // Only the owner (user id 1) may see the admin tabs. Everyone else sees Account only.
  const isAdmin = currentUser?.id === 1;

  // State
  const [loading, setLoading] = useState(false);
  const [shopeeStatusLoading, setShopeeStatusLoading] = useState(false);
  const [shopeeStatus, setShopeeStatus] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Shopify credentials
  const [shopifyShopUrl, setShopifyShopUrl] = useState('');
  const [shopifyAccessToken, setShopifyAccessToken] = useState('');
  
  // ECPay settings
  const [ecpayMerchantId, setEcpayMerchantId] = useState('');
  const [ecpayHashKey, setEcpayHashKey] = useState('');
  const [ecpayHashIv, setEcpayHashIv] = useState('');
  const [ecpayEnvironment, setEcpayEnvironment] = useState('production');
  const [ecpaySenderName, setEcpaySenderName] = useState('邱泰滕');
  const [ecpaySenderPhone, setEcpaySenderPhone] = useState('0988528467');

  const [credentialType, setCredentialType] = useState<'default' | 'personal'>('default');
  const [defaultCredentialsConfigured, setDefaultCredentialsConfigured] = useState(false);
  const [personalCredentialsConfigured, setPersonalCredentialsConfigured] = useState(false);

  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [sheetsTesting, setSheetsTesting] = useState(false);
  const [sheetsSyncing, setSheetsSyncing] = useState(false);
  const [sheetsMessage, setSheetsMessage] = useState<{text: string, type: 'success' | 'error' | 'info' | 'warning' | null}>({
    text: '',
    type: null
  });

  

  useEffect(() => {
    const fetchGoogleSheetsSettings = async () => {
      try {
        const response = await api.get('/api/settings/google-sheets');
        if (response.data && response.data.spreadsheetId) {
          setSpreadsheetId(response.data.spreadsheetId);
        }
      } catch (error) {
        console.error('Failed to fetch Google Sheets settings:', error);
      }
    };

    // Call this function when the Google Sheets tab is selected
    if (tabValue === 3) { // Assuming this is the 4th tab (index 3)
      fetchGoogleSheetsSettings();
    }
  }, [tabValue]);
  
  useEffect(() => {
    if (!isAdmin) return; // Only the owner loads platform credentials
    if (shopeeAuthenticated) {
      fetchShopeeStatus();
    }

    if (shopifyAuthenticated) {
      fetchShopifyCredentials();
    }

    fetchEcpaySettings();
  }, [shopeeAuthenticated, shopifyAuthenticated, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    // When credential type changes, fetch the settings for that type
    fetchEcpaySettings(credentialType);
  }, [credentialType, isAdmin]);
  
  // Non-admins can only ever land on the Account tab (index 4).
  useEffect(() => {
    if (!isAdmin) {
      setTabValue(4);
    }
  }, [isAdmin]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (!isAdmin) return; // Guard: non-admins stay on Account
    setTabValue(newValue);
  };
  
  // Fetch Shopee status
  const fetchShopeeStatus = async () => {
    setShopeeStatusLoading(true);
    try {
      const response = await api.get('/api/shopee/auth/check');
      // Also fetch the authorization grant expiry (the 365-day-max grant, not the 4h token)
      let authorization = null;
      try {
        const authResp = await api.get('/api/shopee/auth/authorization');
        authorization = authResp.data;
      } catch (authError) {
        console.error('Failed to fetch Shopee authorization info:', authError);
      }
      setShopeeStatus({ ...response.data, authorization });
    } catch (error) {
      console.error('Failed to fetch Shopee status:', error);
    } finally {
      setShopeeStatusLoading(false);
    }
  };
  
  // Fetch Shopify credentials
  const fetchShopifyCredentials = async () => {
    try {
      const response = await api.get('/api/shopify/credentials');
      if (response.data) {
        setShopifyShopUrl(response.data.shopUrl || '');
        // Don't set access token for security reasons
      }
    } catch (error) {
      console.error('Failed to fetch Shopify credentials:', error);
    }
  };
  
  // Fetch ECPay settings
  const fetchEcpaySettings = async (type: 'default' | 'personal' = 'default') => {
    try {
      const response = await api.get(`/api/ecpay/settings/${type}`);
      if (response.data) {
        setEcpayMerchantId(response.data.merchantId || '');
        setEcpayHashKey(response.data.hashKey || '');
        setEcpayHashIv(response.data.hashIv || '');
        setEcpayEnvironment(response.data.environment || 'production');
        setEcpaySenderName(response.data.senderName || '邱泰滕');
        setEcpaySenderPhone(response.data.senderPhone || '0988528467');
      }
    } catch (error) {
      console.error(`Failed to fetch ECPay settings for ${type}:`, error);
    }
  };

  const checkCredentialStatus = async () => {
    try {
      // Check default credentials
      const defaultResponse = await api.get('/api/ecpay/settings/default');
      setDefaultCredentialsConfigured(
        !!(defaultResponse.data?.merchantId && 
           defaultResponse.data?.hashKey && 
           defaultResponse.data?.hashIv)
      );

      // Check personal credentials
      const personalResponse = await api.get('/api/ecpay/settings/personal');
      setPersonalCredentialsConfigured(
        !!(personalResponse.data?.merchantId && 
           personalResponse.data?.hashKey && 
           personalResponse.data?.hashIv)
      );
    } catch (error) {
      console.error('Failed to check credential status:', error);
    }
  };
  
  // Handle Shopee authentication
  const handleShopeeAuth = async () => {
    try {
      const authUrl = await getShopeeAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to get Shopee auth URL:', error);
      enqueueSnackbar('Failed to get Shopee auth URL', { variant: 'error' });
    }
  };
  
  // Handle Shopee disconnect
  const handleShopeeDisconnect = async () => {
    try {
      await disconnectShopee();
      setShopeeStatus(null);
      enqueueSnackbar('Shopee disconnected successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to disconnect Shopee:', error);
      enqueueSnackbar('Failed to disconnect Shopee', { variant: 'error' });
    }
  };
  
  // Handle Shopify credentials save
  const handleSaveShopifyCredentials = async () => {
    if (!shopifyShopUrl || !shopifyAccessToken) {
      enqueueSnackbar('Please fill in all fields', { variant: 'error' });
      return;
    }
    
    setLoading(true);
    try {
      await saveShopifyCredentials(shopifyShopUrl, shopifyAccessToken);
      enqueueSnackbar('Shopify credentials saved successfully', { variant: 'success' });
      // Clear access token for security
      setShopifyAccessToken('');
    } catch (error) {
      console.error('Failed to save Shopify credentials:', error);
      enqueueSnackbar('Failed to save Shopify credentials', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Shopify disconnect
  const handleShopifyDisconnect = async () => {
    try {
      await disconnectShopify();
      setShopifyShopUrl('');
      setShopifyAccessToken('');
      enqueueSnackbar('Shopify disconnected successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to disconnect Shopify:', error);
      enqueueSnackbar('Failed to disconnect Shopify', { variant: 'error' });
    }
  };
  
  // Handle ECPay settings save
  const handleSaveEcpaySettings = async () => {
    if (!ecpayMerchantId || !ecpayHashKey || !ecpayHashIv) {
      enqueueSnackbar('Please fill in all ECPay fields', { variant: 'error' });
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/api/ecpay/settings/${credentialType}`, {
        merchantId: ecpayMerchantId,
        hashKey: ecpayHashKey,
        hashIv: ecpayHashIv,
        environment: ecpayEnvironment,
        senderName: ecpaySenderName,
        senderPhone: ecpaySenderPhone
      });
      
      enqueueSnackbar(
        `${credentialType === 'personal' ? 'Personal' : 'Company'} ECPay settings saved successfully`, 
        { variant: 'success' }
      );
      
      // Refresh credential status
      await checkCredentialStatus();
    } catch (error) {
      console.error('Failed to save ECPay settings:', error);
      enqueueSnackbar('Failed to save ECPay settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  // FIXED Excel Upload Handler for your specific Excel structure
// Replace your handleExcelFileUpload function with this version

const handleExcelFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file) return;
  
  setSheetsLoading(true);
  setSheetsMessage({ text: 'Processing Excel file...', type: 'info' });
  
  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, {
      type: 'array',
      cellStyles: true,
      cellFormula: true,
      cellDates: true,
      cellNF: true,
      cellHTML: false,
      cellText: true
    });
    
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    
    console.log(`📊 Worksheet range: ${worksheet['!ref']}, rows: ${range.e.r + 1}, columns: ${range.e.c + 1}`);
    
    // Find the header row by looking for "Id" column
    let headerRowIndex = -1;
    let idColIndex = -1;
    let linkColIndices: number[] = [];
    
    // Search for the row with "Id" header
    for (let rowIndex = 0; rowIndex <= Math.min(20, range.e.r); rowIndex++) {
      for (let colIndex = 0; colIndex <= range.e.c; colIndex++) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = worksheet[cellRef];
        
        if (cell && cell.v) {
          const cellValue = String(cell.v).trim();
          
          // Look for Id column
          if (cellValue === 'Id' || cellValue === 'id' || cellValue === 'ID') {
            headerRowIndex = rowIndex;
            idColIndex = colIndex;
            console.log(`✅ Found header row ${rowIndex + 1} with Id in column ${XLSX.utils.encode_col(colIndex)}`);
            break;
          }
        }
      }
      if (headerRowIndex !== -1) break;
    }
    
    if (headerRowIndex === -1 || idColIndex === -1) {
      throw new Error('Could not find header row with "Id" column');
    }
    
    // Now scan the header row for link columns
    // Look for columns with "Link", "link", "URL", "url", "2nd", or similar
    for (let colIndex = 0; colIndex <= range.e.c; colIndex++) {
      const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: colIndex });
      const cell = worksheet[cellRef];
      
      if (cell && cell.v) {
        const cellValue = String(cell.v).trim().toLowerCase();
        
        // Detect link columns by header name
        if (
          cellValue === 'link' || 
          cellValue === 'url' ||
          cellValue === '2nd' ||
          cellValue === 'link2' ||
          cellValue === 'second link' ||
          cellValue.includes('link') ||
          cellValue.includes('url')
        ) {
          linkColIndices.push(colIndex);
          console.log(`🔗 Found link column: "${cell.v}" at column ${XLSX.utils.encode_col(colIndex)} (index ${colIndex})`);
        }
      }
    }
    
    // Sort link columns LEFT TO RIGHT (by column index)
    linkColIndices.sort((a, b) => a - b);
    
    console.log('\n📋 DETECTION RESULTS:');
    console.log(`Header row: ${headerRowIndex + 1}`);
    console.log(`Id column: ${XLSX.utils.encode_col(idColIndex)} (index ${idColIndex})`);
    console.log(`Link columns found (LEFT TO RIGHT):`);
    linkColIndices.forEach((colIndex, i) => {
      const headerCellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: colIndex });
      const headerCell = worksheet[headerCellRef];
      console.log(`  ${i + 1}. Column ${XLSX.utils.encode_col(colIndex)} (index ${colIndex}) - Header: "${headerCell.v}"`);
    });
    
    if (linkColIndices.length === 0) {
      throw new Error('Could not find any link columns in the header row');
    }
    
    // Helper function to extract link from a cell
    const extractLinkFromCell = (cell: any): string => {
      if (!cell) return '';
      
      let link = '';
      
      // Check for hyperlink (SheetJS stores hyperlinks in "l" property)
      if (cell.l && cell.l.Target) {
        link = cell.l.Target;
      } 
      // Check for h property (HTML hyperlinks)
      else if (cell.h) {
        const matchHref = /<a href="([^"]+)"/.exec(cell.h);
        if (matchHref && matchHref[1]) {
          link = matchHref[1];
        }
      }
      // Check for HYPERLINK formula
      else if (cell.f && cell.f.toLowerCase().includes('hyperlink(')) {
        const matchFormula = /HYPERLINK\s*\(\s*"([^"]+)"/.exec(cell.f);
        if (matchFormula && matchFormula[1]) {
          link = matchFormula[1];
        }
      }
      // Fallback to cell value
      else if (cell.v) {
        const value = String(cell.v).trim();
        // Only use the value if it looks like a URL
        if (value.startsWith('http://') || value.startsWith('https://') || value.includes('.')) {
          link = value;
        }
      }
      
      return link;
    };
    
    // Process data rows
    const products = [];
    let skippedRows = 0;
    let rowsWithoutLink = 0;
    
    for (let rowIndex = headerRowIndex + 1; rowIndex <= range.e.r; rowIndex++) {
      const idCellRef = XLSX.utils.encode_cell({ r: rowIndex, c: idColIndex });
      const idCell = worksheet[idCellRef];
      
      if (!idCell || !idCell.v) {
        skippedRows++;
        continue;
      }
      
      const id = String(idCell.v).trim();
      if (!id) {
        skippedRows++;
        continue;
      }
      
      // Extract links from ALL detected link columns IN ORDER (left to right)
      const allLinks: string[] = [];
      
      linkColIndices.forEach((linkColIndex) => {
        const linkCellRef = XLSX.utils.encode_cell({ r: rowIndex, c: linkColIndex });
        const linkCell = worksheet[linkCellRef];
        const extractedLink = extractLinkFromCell(linkCell);
        
        if (extractedLink) {
          allLinks.push(extractedLink);
        }
      });
      
      // Log first few products for debugging
      if (products.length < 3) {
        console.log(`\n🔍 Product ${products.length + 1} (ID: ${id}):`);
        allLinks.forEach((link, idx) => {
          const colLetter = XLSX.utils.encode_col(linkColIndices[idx]);
          console.log(`  Link ${idx + 1} from column ${colLetter}: ${link.substring(0, 80)}...`);
        });
      }
      
      if (allLinks.length === 0) {
        rowsWithoutLink++;
      }
      
      // Store product with links
      products.push({
        id,
        link: allLinks[0] || '', // First link (leftmost link column)
        allLinks: allLinks.length > 0 ? allLinks : undefined
      });
    }
    
    console.log(`\n✅ Processed ${products.length} products`);
    console.log(`⚠️  Skipped ${skippedRows} rows (no ID)`);
    console.log(`📊 ${products.length - rowsWithoutLink} products with links, ${rowsWithoutLink} without links`);
    
    // Store in localStorage
    try {
      localStorage.setItem('productLinksData', JSON.stringify(products));
      console.log(`💾 Saved to localStorage`);
    } catch (storageError) {
      console.error('localStorage quota exceeded, clearing and retrying...');
      localStorage.removeItem('productLinksData');
      
      try {
        localStorage.setItem('productLinksData', JSON.stringify(products));
      } catch (retryError) {
        throw new Error('Data too large for localStorage. Consider using Google Sheets integration instead.');
      }
    }
    
    // Clear session storage cache
    sessionStorage.clear();
    console.log(`🗑️  Cleared sessionStorage cache`);
    
    setSheetsMessage({
      text: `Successfully processed ${products.length} products from Excel file (${linkColIndices.length} link column${linkColIndices.length > 1 ? 's' : ''} detected)`,
      type: 'success'
    });
    
    enqueueSnackbar(`Imported ${products.length} product links from Excel file`, { 
      variant: 'success' 
    });
    
    event.target.value = '';
    
  } catch (error) {
    console.error('Error processing Excel file:', error);
    setSheetsMessage({
      text: `Error: ${error instanceof Error ? error.message : 'Unknown error processing file'}`,
      type: 'error'
    });
    enqueueSnackbar('Failed to process Excel file', { variant: 'error' });
  } finally {
    setSheetsLoading(false);
  }
};
  
  // Add this function to test if the Excel data is working
  const handleTestExcelData = async () => {
    setSheetsTesting(true);
    try {
      const storedData = localStorage.getItem('productLinksData');
      
      if (!storedData) {
        throw new Error('No product links data found. Please upload an Excel file first.');
      }
      
      const products = JSON.parse(storedData);
      
      // Validate a few sample links
      let validLinks = 0;
      let invalidLinks = 0;
      const samplesToCheck = Math.min(5, products.length);
      
      for (let i = 0; i < samplesToCheck; i++) {
        const product = products[i];
        if (product.link && (product.link.includes('.') || product.link.startsWith('http'))) {
          validLinks++;
        } else {
          invalidLinks++;
        }
      }
      
      if (validLinks > 0) {
        setSheetsMessage({
          text: `Found ${products.length} products in Excel data. Sample links look valid.`,
          type: 'success'
        });
      } else {
        setSheetsMessage({
          text: `Found ${products.length} products, but links may not be valid. Please check your Excel file.`,
          type: 'warning'
        });
      }
      
      enqueueSnackbar(`Tested ${samplesToCheck} product links: ${validLinks} valid, ${invalidLinks} invalid`, {
        variant: validLinks > 0 ? 'success' : 'warning'
      });
    } catch (error) {
      console.error('Error testing Excel data:', error);
      setSheetsMessage({
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
      enqueueSnackbar('Failed to test Excel data', { variant: 'error' });
    } finally {
      setSheetsTesting(false);
    }
  };
  // Handle ECPay test connection
  const handleTestEcpayConnection = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/ecpay/test-connection');
      
      if (response.data.success) {
        enqueueSnackbar('ECPay connection test successful', { variant: 'success' });
      } else {
        enqueueSnackbar(`ECPay connection test failed: ${response.data.message}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Failed to test ECPay connection:', error);
      enqueueSnackbar('Failed to test ECPay connection', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoogleSheetsSettings = async () => {
    if (!spreadsheetId) {
      enqueueSnackbar('Please enter a Spreadsheet ID', { variant: 'error' });
      return;
    }
  
    setSheetsLoading(true);
    try {
      // Persist config to the backend (accepts a full URL or a bare id).
      await api.post('/api/settings/google-sheets', {
        spreadsheetId,
        sheetName: 'Cost/Profit',
      });

      // Switch the active source to Google Sheets and pull a fresh copy.
      await productLinksService.useGoogleSheets();

      setSheetsMessage({
        text: 'Google Sheets settings saved successfully',
        type: 'success'
      });
      enqueueSnackbar('Google Sheets settings saved successfully', { variant: 'success' });
    } catch (error: unknown) {
      console.error('Failed to save Google Sheets settings:', error);
      let errorMessage = 'Failed to save Google Sheets settings';
      
      if (error instanceof Error) {
        errorMessage += ': ' + error.message;
      }
      
      setSheetsMessage({
        text: errorMessage,
        type: 'error'
      });
      enqueueSnackbar('Failed to save Google Sheets settings', { variant: 'error' });
    } finally {
      setSheetsLoading(false);
    }
  };
  
  // Force the backend to re-sync the sheet into the product_links DB table now.
  const handleSyncProductLinksNow = async () => {
    setSheetsSyncing(true);
    try {
      const result = await productLinksService.syncNow();
      setSheetsMessage({
        text: `Synced ${result.synced ?? 0} products (${result.withLink ?? 0} with links) to the database.`,
        type: 'success'
      });
      enqueueSnackbar('Product links synced to database', { variant: 'success' });
    } catch (error: unknown) {
      console.error('Failed to sync product links:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setSheetsMessage({ text: 'Sync failed: ' + msg, type: 'error' });
      enqueueSnackbar('Failed to sync product links', { variant: 'error' });
    } finally {
      setSheetsSyncing(false);
    }
  };

  // Replace the existing handleTestGoogleSheetsConnection with this function
  const handleTestGoogleSheetsConnection = async () => {
    if (!spreadsheetId) {
      enqueueSnackbar('Please enter a Spreadsheet ID', { variant: 'error' });
      return;
    }
  
    setSheetsTesting(true);
    try {
      // Test the connection against the entered spreadsheet id/URL via the backend.
      const result = await productLinksService.testConnection(spreadsheetId, 'Cost/Profit');
  
      if (result.success) {
        setSheetsMessage({
          text: `Connection successful! Found ${result.rowCount} product entries.`,
          type: 'success'
        });
        enqueueSnackbar('Google Sheets connection test successful', { variant: 'success' });
      } else {
        setSheetsMessage({
          text: 'Connection test failed: ' + (result.error || 'Unknown error'),
          type: 'error'
        });
        enqueueSnackbar('Google Sheets connection test failed', { variant: 'error' });
      }
    } catch (error: unknown) {
      console.error('Failed to test Google Sheets connection:', error);
      let errorMessage = 'Connection test failed';
      
      if (error instanceof Error) {
        errorMessage += ': ' + error.message;
      }
      
      setSheetsMessage({
        text: errorMessage,
        type: 'error'
      });
      enqueueSnackbar('Failed to test Google Sheets connection', { variant: 'error' });
    } finally {
      setSheetsTesting(false);
    }
  };

  // Copy spreadsheet URL to clipboard
  const handleCopySpreadsheetUrl = () => {
    if (!spreadsheetId) return;
    
    const url = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
    navigator.clipboard.writeText(url);
    
    enqueueSnackbar('Spreadsheet URL copied to clipboard', { variant: 'success' });
  };

  // Open spreadsheet in new tab
  const handleOpenSpreadsheet = () => {
    if (!spreadsheetId) return;
    
    const url = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    // Only run when the ProductLinks tab is selected
    if (tabValue === 3) {
      const savedSpreadsheetId = localStorage.getItem('productLinksSpreadsheetId');
      if (savedSpreadsheetId) {
        setSpreadsheetId(savedSpreadsheetId);
      }
    }
  }, [tabValue]);
  
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        aria-label="settings tabs"
        centered
      >
        {isAdmin && <Tab label="Platform Connections" value={0} />}
        {isAdmin && <Tab label="ECPay Settings" value={1} />}
        {isAdmin && <Tab label="General Settings" value={2} />}
        {isAdmin && <Tab label="Product Links" value={3} />}
        <Tab label="Account" value={4} icon={<AccountIcon />} iconPosition="start" />
      </Tabs>
        
        {isAdmin && <>
        {/* Platform Connections Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Shopee */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }} elevation={3}>
                <Typography variant="h6" gutterBottom>
                  Shopee Connection
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {shopeeAuthenticated ? (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Connected to Shopee
                    </Alert>
                    
                    {shopeeStatusLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : shopeeStatus ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          Shop ID: {shopeeStatus.tokenData?.shopId}
                        </Typography>
                        {shopeeStatus.authorization?.authDaysLeft != null ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: shopeeStatus.authorization.authDaysLeft <= 30 ? 'error.main' : 'text.primary'
                            }}
                          >
                            Authorization: {shopeeStatus.authorization.authDaysLeft} days left
                            {shopeeStatus.authorization.authExpireTime
                              ? ` (expires ${new Date(shopeeStatus.authorization.authExpireTime * 1000).toLocaleDateString()})`
                              : ''}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Authorization: unknown
                          </Typography>
                        )}
                      </Box>
                    ) : null}
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchShopeeStatus}
                        disabled={shopeeStatusLoading}
                      >
                        Refresh Status
                      </Button>
                      
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleShopeeDisconnect}
                      >
                        Disconnect
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Not connected to Shopee
                    </Alert>
                    
                    <Button
                      variant="contained"
                      startIcon={<LoginIcon />}
                      onClick={handleShopeeAuth}
                    >
                      Connect to Shopee
                    </Button>
                  </>
                )}
              </Paper>
            </Grid>
            
            {/* Shopify Multi-Store */}
          <Grid item xs={12}>
            <ShopifyMultiStoreSection />
          </Grid>
          </Grid>
        </TabPanel>
        
        {/* ECPay Settings Tab */}
        <TabPanel value={tabValue} index={1}>
  <Paper sx={{ p: 3 }} elevation={3}>
    <Typography variant="h6" gutterBottom>
      ECPay Credentials Management
    </Typography>
    <Divider sx={{ mb: 2 }} />
    
    {/* Info Alert */}
    <Alert severity="info" sx={{ mb: 3 }}>
      <Typography variant="body2">
        <strong>Default Credentials:</strong> Used for regular Shopify orders and company payments
        <br />
        <strong>Personal Credentials:</strong> Used specifically for outlet sales when clicking "出貨"
      </Typography>
    </Alert>
    
    {/* Add state for credential type selector */}
    <Box sx={{ mb: 3 }}>
      <Chip 
        label="Default Settings" 
        color={credentialType === 'default' ? 'primary' : 'default'}
        onClick={() => {
          setCredentialType('default');
          fetchEcpaySettings('default');
        }}
        sx={{ mr: 1 }}
        clickable
      />
      <Chip 
        label="Personal Settings" 
        color={credentialType === 'personal' ? 'secondary' : 'default'}
        onClick={() => {
          setCredentialType('personal');
          fetchEcpaySettings('personal');
        }}
        clickable
      />
    </Box>
    
    <Typography variant="subtitle1" gutterBottom>
      {credentialType === 'personal' ? 'Personal Account' : 'Company Account'} ECPay Configuration
    </Typography>
    
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextField
          label="Merchant ID"
          value={ecpayMerchantId}
          onChange={(e) => setEcpayMerchantId(e.target.value)}
          fullWidth
          margin="normal"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          label="Hash Key"
          value={ecpayHashKey}
          onChange={(e) => setEcpayHashKey(e.target.value)}
          type="password"
          fullWidth
          margin="normal"
          helperText="Will show ******** if already set"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          label="Hash IV"
          value={ecpayHashIv}
          onChange={(e) => setEcpayHashIv(e.target.value)}
          type="password"
          fullWidth
          margin="normal"
          helperText="Will show ******** if already set"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="ecpay-environment-label">Environment</InputLabel>
          <Select
            labelId="ecpay-environment-label"
            value={ecpayEnvironment}
            label="Environment"
            onChange={(e) => setEcpayEnvironment(e.target.value)}
          >
            <MenuItem value="test">Test</MenuItem>
            <MenuItem value="production">Production</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          label="Sender Name"
          value={ecpaySenderName}
          onChange={(e) => setEcpaySenderName(e.target.value)}
          fullWidth
          margin="normal"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          label="Sender Phone"
          value={ecpaySenderPhone}
          onChange={(e) => setEcpaySenderPhone(e.target.value)}
          fullWidth
          margin="normal"
        />
      </Grid>
    </Grid>
    
    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={handleSaveEcpaySettings}
        disabled={loading || !ecpayMerchantId || !ecpayHashKey || !ecpayHashIv}
        color={credentialType === 'personal' ? 'secondary' : 'primary'}
      >
        {loading ? <CircularProgress size={24} /> : `Save ${credentialType === 'personal' ? 'Personal' : 'Company'} Settings`}
      </Button>
      
      <Button
        variant="outlined"
        onClick={handleTestEcpayConnection}
        disabled={loading || !ecpayMerchantId || !ecpayHashKey || !ecpayHashIv}
      >
        Test Connection
      </Button>
    </Box>
    
    {/* Show current credential status */}
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Credential Status:
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Company Credentials
            </Typography>
            <Chip 
              label={defaultCredentialsConfigured ? "Configured" : "Not Configured"} 
              color={defaultCredentialsConfigured ? "success" : "error"} 
              size="small" 
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Personal Credentials
            </Typography>
            <Chip 
              label={personalCredentialsConfigured ? "Configured" : "Not Configured"} 
              color={personalCredentialsConfigured ? "success" : "error"} 
              size="small" 
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
    
    <Box sx={{ mt: 3 }}>
      <Alert severity="warning">
        <Typography variant="subtitle2">
          Usage Information
        </Typography>
        <Typography variant="body2">
          • <strong>Company credentials</strong> will be used for regular Shopify orders and general ECPay operations
          <br />
          • <strong>Personal credentials</strong> will be used when shipping outlet sales (clicking "出貨" in sales history)
          <br />
          • Make sure both credential sets are configured for full functionality
        </Typography>
      </Alert>
    </Box>
  </Paper>
</TabPanel>
        
        {/* General Settings Tab */}
        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: 3 }} elevation={3}>
            <Typography variant="h6" gutterBottom>
              General Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Alert severity="info">
              Additional settings can be added here.
            </Alert>
          </Paper>
        </TabPanel>
        // Replace your current TabPanel for index 3 (Product Links) with this corrected version:

<TabPanel value={tabValue} index={3}>
  <Paper sx={{ p: 3 }} elevation={3}>
    <Typography variant="h6" gutterBottom>
      Product Links Integration
    </Typography>
    <Divider sx={{ mb: 2 }} />
    
    {/* Excel File Uploader Section */}
    <Box sx={{ my: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px dashed rgba(0, 0, 0, 0.12)' }}>
      <Typography variant="h6" gutterBottom>
        Upload Product Links Excel File
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload an Excel file (.xlsx or .xls) containing your product links. 
        The file should include columns for "Id" and "Link".
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadFileIcon />}
          disabled={sheetsLoading}
        >
          {sheetsLoading ? <CircularProgress size={24} /> : 'Upload Excel File'}
          <input
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleExcelFileUpload}
          />
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<LinkIcon />}
          onClick={handleTestExcelData}
          disabled={sheetsTesting}
          sx={{ ml: 2 }}
        >
          {sheetsTesting ? <CircularProgress size={24} /> : 'Test Excel Data'}
        </Button>
      </Box>
    </Box>

    {/* Add a divider between the Excel uploader and Google Sheets section */}
    <Divider sx={{ my: 3 }} />

    <Typography variant="h6" gutterBottom>
      Alternative: Google Sheets Integration
    </Typography>
    
    <Typography variant="body1" paragraph>
      Connect to your Google Sheets spreadsheet containing product links. The spreadsheet must be published to the web as CSV for this integration to work.
    </Typography>
    
    {/* Instructions for publishing to the web */}
    <Alert severity="info" sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        How to publish your spreadsheet to the web:
      </Typography>
      <Typography variant="body2">
        1. Open your Google Sheet
        <br />
        2. Click File → Share → Publish to web
        <br />
        3. Select "Entire Document" and format "Comma-separated values (.csv)"
        <br />
        4. Click "Publish"
        <br />
        5. Copy the ID from your Google Sheet URL: https://docs.google.com/spreadsheets/d/[THIS-IS-THE-ID]/edit
      </Typography>
    </Alert>
    
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          label="Google Spreadsheet ID"
          value={spreadsheetId}
          onChange={(e) => setSpreadsheetId(e.target.value)}
          fullWidth
          margin="normal"
          helperText="The ID from your Google Sheet URL: https://docs.google.com/spreadsheets/d/[THIS-IS-THE-ID]/edit"
          InputProps={{
            endAdornment: spreadsheetId ? (
              <Box sx={{ display: 'flex' }}>
                <Tooltip title="Copy Spreadsheet URL">
                  <IconButton onClick={handleCopySpreadsheetUrl} edge="end">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open Spreadsheet">
                  <IconButton onClick={handleOpenSpreadsheet} edge="end">
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null
          }}
        />
      </Grid>
    </Grid>
    
    {sheetsMessage.text && sheetsMessage.type && (
      <Alert severity={sheetsMessage.type} sx={{ mt: 2, mb: 2 }}>
        {sheetsMessage.text}
      </Alert>
    )}
    
    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={handleSaveGoogleSheetsSettings}
        disabled={sheetsLoading || !spreadsheetId}
      >
        {sheetsLoading ? <CircularProgress size={24} /> : 'Save Settings'}
      </Button>
      
      <Button
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={handleTestGoogleSheetsConnection}
        disabled={sheetsTesting || !spreadsheetId}
      >
        {sheetsTesting ? <CircularProgress size={24} /> : 'Test Connection'}
      </Button>

      <Button
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={handleSyncProductLinksNow}
        disabled={sheetsSyncing}
      >
        {sheetsSyncing ? <CircularProgress size={24} /> : 'Sync Links to Database'}
      </Button>
    </Box>
    
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" gutterBottom>
        Spreadsheet Format Requirements:
      </Typography>
      <Typography component="div" variant="body2">
        <ul>
          <li>Headers must be in row 6</li>
          <li>The spreadsheet should include these columns:
            <Box component="ul" sx={{ ml: 2 }}>
              <li>Id - Product identifier that matches your SKU</li>
              <li>Wholesale, Weight, Service Fee, Delivery Fee, etc. - Your cost calculations</li>
              <li>Link - The URL where you can order the product</li>
            </Box>
          </li>
          <li>The "Id" column should match your product SKUs in the order system</li>
          <li>The "Link" column should contain the full URL for ordering each product</li>
        </ul>
      </Typography>
    </Box>
  </Paper>
</TabPanel>
        </>}
<TabPanel value={tabValue} index={4}>
  <Paper sx={{ p: 3 }} elevation={3}>
    <Typography variant="h6" gutterBottom>
      Account Information
    </Typography>
    <Divider sx={{ mb: 3 }} />
    
    {/* User information */}
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name"
            value={currentUser?.name || ''}
            fullWidth
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Email"
            value={currentUser?.email || ''}
            fullWidth
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="User ID"
            value={currentUser?.id || ''}
            fullWidth
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      </Grid>
    </Box>
    
    <Typography variant="h6" gutterBottom>
      Session Information
    </Typography>
    <Divider sx={{ mb: 3 }} />
    
    <Box sx={{ mb: 4 }}>
      <Alert 
        severity="info" 
        sx={{ mb: 2 }}
        action={
          <Tooltip title="Copy User ID">
            <IconButton 
              size="small" 
              onClick={() => {
                if (currentUser?.id) {
                  navigator.clipboard.writeText(currentUser.id.toString());
                  enqueueSnackbar('User ID copied to clipboard', { variant: 'success' });
                }
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        }
      >
        <Typography variant="body2">
          Session Status: {isAuthenticated ? 'Active' : 'Inactive'}
        </Typography>
        <Typography variant="body2">
          {isAuthenticated 
            ? 'You are currently logged in.' 
            : 'Your session has expired. Please log in again.'}
        </Typography>
      </Alert>
    </Box>
    
    <Typography variant="h6" gutterBottom>
      Account Actions
    </Typography>
    <Divider sx={{ mb: 3 }} />
    
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Button
        variant="contained"
        color="error"
        startIcon={<LogoutIcon />}
        onClick={() => {
          logout();
          enqueueSnackbar('You have been logged out successfully', { variant: 'success' });
          navigate('/auth');
        }}
      >
        Log Out
      </Button>
    </Box>
  </Paper>
</TabPanel>
      </Paper>
    </Box>
  );
};

export default SettingsPage;